import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/tables/Table";
import { DaoGovernorProfile, UserRole } from '@/types';
import { useToast } from '@/contexts/ToastContext';
import { useLogger } from '@/contexts/LogContext';
import { API_BASE_URL } from '@/config';
import { LogLevel, LogCategory } from '@/types/logs';
import { Loader2, Search } from 'lucide-react';
import { useWallet } from '@/contexts/WalletContext';
import { contractAddresses } from '@/data/contractAddresses';
import KalaKrutGovernorABI from '@/abis/KalaKrutGovernor.json';
import { ethers, EventLog } from 'ethers';

const ActionButton: React.FC<{ onClick: () => void; className: string; children: React.ReactNode }> = ({ onClick, className, children }) => (
  <button
    onClick={onClick}
    className={`text-xs font-semibold px-2.5 py-1 rounded-md transition-colors ${className}`}>
    {children}
  </button>
);

const TableActions: React.FC<{ 
  governor: DaoGovernorProfile;
  role: UserRole; 
  onAction: (action: string, governorId: string) => void;
}> = ({ governor, role, onAction }) => {

  const handleAction = (action: string) => {
    onAction(action, governor.id);
  };

  if (role === UserRole.SYSTEM_ADMIN_LIVE || role === UserRole.ADMIN) {
    return (
        <div className="space-x-2">
          <ActionButton onClick={() => handleAction('view')} className="bg-blue-500/20 text-blue-300 hover:bg-blue-500/40">View</ActionButton>
          <ActionButton onClick={() => handleAction('delete')} className="bg-red-500/20 text-red-300 hover:bg-red-500/40">Delete</ActionButton>
        </div>
      );
  }
  
  return <ActionButton onClick={() => handleAction('view')} className="bg-gray-500/20 text-gray-300 hover:bg-gray-500/40">View Profile</ActionButton>;
};

const DaoGovernorRealDataTable = ({ currentUserRole, currentUserId }: { currentUserRole: UserRole, currentUserId: string }) => {
  const [allGovernors, setAllGovernors] = useState<DaoGovernorProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const { notify } = useToast();
  const { addLog } = useLogger();
  const { provider, isConnected } = useWallet();
  const PAGE_SIZE = 10;

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    addLog("Starting DAO governor data fetch.", LogLevel.INFO, LogCategory.DATA);

    try {
      let onChainGovernors: DaoGovernorProfile[] = [];
      let offChainGovernors: DaoGovernorProfile[] = [];

      if (provider && isConnected) {
        try {
          const governorContract = new ethers.Contract(contractAddresses.KalaKrutGovernor, KalaKrutGovernorABI.abi, provider);
          
          const proposalEvents = await governorContract.queryFilter(governorContract.filters.ProposalCreated());
          const voteEvents = await governorContract.queryFilter(governorContract.filters.VoteCast());
          
          const governorAddresses = new Set<string>();
          (proposalEvents as EventLog[]).forEach((event) => governorAddresses.add(event.args.proposer));
          (voteEvents as EventLog[]).forEach((event) => governorAddresses.add(event.args.voter));

          onChainGovernors = Array.from(governorAddresses).map(address => ({
            id: address,
            userId: address,
            user: { id: address, name: `${address.substring(0, 6)}...${address.substring(address.length - 4)}`, role: UserRole.DAO_GOVERNOR },
            proposals_created: 0, // This would require more detailed event processing
            votes_cast: 0, // This would require more detailed event processing
            verified: true, // Assumed verified if they interacted with the contract
          }));

          addLog(`Fetched ${onChainGovernors.length} unique on-chain governors.`, LogLevel.INFO, LogCategory.DATA);
        } catch (e) {
          const onChainError = "Could not load on-chain governor data. Check wallet connection or contract status.";
          addLog(`On-chain fetch failed: ${(e as Error).message}`, LogLevel.ERROR, LogCategory.DATA);
          setError(onChainError);
          notify(onChainError, 'error');
        }
      }

      try {
        const response = await fetch(`${API_BASE_URL}/api/dao-governors`, { credentials: 'include' });
        if (response.ok) {
          const apiData: DaoGovernorProfile[] = await response.json();
          if (Array.isArray(apiData)) {
            offChainGovernors = apiData;
            addLog(`Fetched ${apiData.length} off-chain governors from REAL API.`, LogLevel.INFO, LogCategory.API);
          } else {
            throw new Error('Live API did not return an array');
          }
        } else {
            throw new Error('Live API failed or returned non-200 status');
        }
      } catch (e) {
        addLog(`Live API failed: ${(e as Error).message}. Falling back to mock data.`, LogLevel.WARN, LogCategory.API);
        notify('Live data failed. Displaying static fallback data.', 'warn');
        try {
            const mockResponse = await fetch('/dao-governors.json');
            if (mockResponse.ok) {
                const mockData: DaoGovernorProfile[] = await mockResponse.json();
                if (Array.isArray(mockData)) {
                    offChainGovernors = mockData;
                } else {
                    throw new Error('Mock data is not an array');
                }
            } else {
                throw new Error('Failed to fetch mock data');
            }
        } catch (mockError) {
             const finalError = `Critical: Failed to load any off-chain governor data: ${(mockError as Error).message}`;
             addLog(finalError, LogLevel.CRITICAL, LogCategory.API);
             setError(prev => prev ? `${prev} | ${finalError}` : finalError);
             notify(finalError, 'error');
        }
      }
      
      // Merge and deduplicate
      const all = [...onChainGovernors, ...offChainGovernors];
      const uniqueGovernors = Array.from(new Map(all.map(g => [g.id, g])).values());
      setAllGovernors(uniqueGovernors.sort((a, b) => (a.user?.name || '').localeCompare(b.user?.name || '')));

    } catch (e) {
      const genericError = `Failed to load governor data: ${(e as Error).message}`;
      setError(genericError);
      addLog(genericError, LogLevel.ERROR, LogCategory.DATA);
    } finally {
      setIsLoading(false);
    }
  }, [provider, isConnected, notify, addLog]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);


  const handleAction = async (action: string, governorId: string) => {
    if (action === 'view') {
        notify(`Viewing profile for governor ${governorId}.`, 'info');
    } else if (action === 'delete') {
        if (!window.confirm('Are you sure you want to remove this governor?')) return;
        
        addLog(`User ${currentUserId} initiated delete for governor ${governorId}`, LogLevel.INFO, LogCategory.DAO);
        try {
            const response = await fetch(`${API_BASE_URL}/api/dao-governors/${governorId}`, { method: 'DELETE', credentials: 'include' });
            if (!response.ok) throw new Error('Failed to delete governor from API');
            setAllGovernors(current => current.filter(g => g.id !== governorId));
            notify(`Governor ${governorId} removed.`, 'success');
            addLog(`Governor ${governorId} successfully removed by user ${currentUserId}`, LogLevel.INFO, LogCategory.DAO);
        } catch (err) {
            notify((err as Error).message, 'error');
            addLog(`Failed to remove governor ${governorId}: ${(err as Error).message}`, LogLevel.ERROR, LogCategory.DAO);
        }
    }
  };

  const filteredGovernors = useMemo(() => {
      return allGovernors.filter(g => 
        (g.user?.name || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
  }, [allGovernors, searchTerm]);

  const displayedGovernors = useMemo(() => {
      return filteredGovernors.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  }, [filteredGovernors, page]);

  const hasMore = page * PAGE_SIZE < filteredGovernors.length;

  if (isLoading && allGovernors.length === 0) return (
    <div className="flex justify-center items-center h-48">
      <Loader2 className="w-8 h-8 text-kala-secondary animate-spin" />
      <p className="ml-4 text-kala-400">Loading DAO governors...</p>
    </div>
  );

  return (
    <div className="bg-kala-900 text-white rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">DAO Governors</h2>
            <div className="relative">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-kala-500" />
                <input
                    type="text"
                    placeholder="Search governors..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className="bg-kala-800 border border-kala-700 rounded-full pl-10 pr-4 py-2 text-sm text-white focus:ring-1 focus:ring-kala-secondary outline-none w-64"
                />
            </div>
        </div>
        {error && <div className="p-4 mb-4 bg-yellow-500/10 text-yellow-400 text-center text-sm rounded-lg">Warning: {error}</div>}
        {!isConnected && (
            <div className="p-2 mb-4 bg-kala-900/30 text-kala-500 text-center text-[10px] uppercase tracking-widest border-y border-kala-700">
            Note: Wallet disconnected. On-chain data is unavailable.
            </div>
        )}

      <Table>
        <TableCaption>A list of all DAO governors.</TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Proposals Created</TableHead>
            <TableHead>Votes Cast</TableHead>
            <TableHead>Verified</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {displayedGovernors.map((governor) => (
            <TableRow key={governor.id}>
              <TableCell>{governor.user?.name || 'N/A'}</TableCell>
              <TableCell>{governor.proposals_created}</TableCell>
              <TableCell>{governor.votes_cast}</TableCell>
              <TableCell>{governor.verified ? 'Yes' : 'No'}</TableCell>
              <TableCell className="text-right">
                  <TableActions governor={governor} role={currentUserRole} onAction={handleAction} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

        {displayedGovernors.length === 0 && !isLoading && (
            <div className="text-center p-8 text-kala-500">
                <p>No governors found for your search term.</p>
            </div>
        )}

      <div className="flex justify-between items-center mt-6">
        <button 
          onClick={() => setPage(p => Math.max(1, p - 1))}
          disabled={page === 1 || isLoading}
          className="px-4 py-2 bg-kala-700 hover:bg-kala-600 text-white font-bold rounded-lg disabled:opacity-50 transition-colors"
        >
          Previous
        </button>
        <span className="text-kala-400">Page {page} of {Math.ceil(filteredGovernors.length / PAGE_SIZE)}</span>
        <button 
          onClick={() => setPage(p => p + 1)}
          disabled={!hasMore || isLoading}
          className="px-4 py-2 bg-kala-700 hover:bg-kala-600 text-white font-bold rounded-lg disabled:opacity-50 transition-colors"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default DaoGovernorRealDataTable;
